
/**
 * Module dependencies.
 */

import { Banner } from 'client/components/core/banner';
import { Container } from 'client/components/core/container';
import { Footer } from 'client/components/core/footer';
import { GetAppModal } from 'client/components/core/get-app-modal';
import { Helmet } from 'react-helmet';
import { Loading, ModalPortal } from '@slyk/design-system';
import { Navbar } from 'client/components/core/navbar';
import { Redirect, useParams } from 'react-router-dom';
import { Task } from 'types/tasks';
import { TaskDetails } from 'client/components/tasks/task-details';
import { compact } from 'lodash';
import { dashboardUrlResolver } from 'client/core/utils/url-resolver';
import { formatCurrency } from 'client/core/utils/formatter';
import { gql, useQuery } from '@apollo/client';
import { ifNotProp, prop, theme } from 'styled-tools';
import { payspaceSignupModes } from '@slyk/types';
import { useBanner } from 'client/hooks/use-banner';
import { usePayspace } from '@slyk/payspace-context';
import { useQueryString } from 'client/hooks/use-query-string';
import { useSlykApp } from 'client/hooks/use-slyk-app';
import { useTranslate } from '@slyk/i18n';
import { useWindowScroll } from 'client/hooks/use-window-scroll';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import payspaceRoutes from '@slyk/config/payspace-routes';
import routes from '@slyk/config/dashboard-routes';
import styled, { css } from 'styled-components';

/**
 * Task details query.
 */

const taskDetailsQuery = gql`
  query TaskDetails($id: ID!, $idFilter: FilterValueType) {
    rewardAsset: defaultBonusAsset {
      assetCode: code
      baseRates {
        quoteAssetCode
        rate
      }
      decimalPlaces
      decimalPlacesToDisplay
      skipTrailingZeros
      symbol
      type
    }

    task(id: $id) {
      amount
      description
      enabled
      id
      media {
        ... on Image {
          status
          type
          uploadId
          url
        }
        ... on Video {
          duration
          playbackId
          previewUrl
          status
          thumbnailUrl
          type
          uploadId
          url
        }
      }
      name
      type
    }

    tasks(
      filter: [{
        name: "enabled",
        value: "true"
      }, {
        name: "id",
        value: $idFilter
      }]
      page: {
        number: 1
        size: 5
      }
      sort: "order"
    ) {
      data {
        amount
        description
        enabled
        id
        media {
          ... on Image {
            status
            type
            uploadId
            url
          }
          ... on Video {
            duration
            playbackId
            previewUrl
            status
            thumbnailUrl
            type
            uploadId
            url
          }
        }
        name
        type
      }
      total
    }
  }
`;

/**
 * `LoadingContainer` styled component.
 */

const LoadingContainer = styled(Container)`
  display: grid;
  grid-template-rows: 1fr;
  left: 50%;
  padding-top: ${prop('navbarHeight', 122)}px;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: transform ${theme('animations.defaultTransition')};
`;

/**
 * `StyledLoading` styled component.
 */

const StyledLoading = styled(Loading)`
  grid-column: 1 / -1;
  grid-row: 1;
  place-self: center;
`;

/**
 * `SectionWrapper` styled component.
 */

const SectionWrapper = styled.div<{ isVisible: boolean; navbarHeight: number }>`
  padding-top: ${prop('navbarHeight', 122)}px;

  ${ifNotProp('isVisible', css`
    display: none;
  `)}
`;

/**
 * Export `BaseTaskDetails` container.
 */

export function BaseTaskDetails(): JSX.Element {
  const { i18n, translate } = useTranslate();
  const locale = i18n.language;
  const { id } = useParams<any>();
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const [navbarHeight, setNavbarHeight] = useState<number>(0);
  const { name, signupMode, slug } = usePayspace();
  const closeModal = useCallback(() => setModalOpen(false), []);
  const { code } = useQueryString() ?? {};
  const isPublicPayspace = signupMode === payspaceSignupModes.public;
  const { data, loading: isLoading } = useQuery(taskDetailsQuery, {
    context: {
      headers: {
        'x-payspace-slug': slug
      }
    },
    skip: !isPublicPayspace,
    variables: {
      id,
      idFilter: `nin:${id}`
    }
  });

  const { task, tasks } = useMemo(() => {
    const normalizedTasks = compact((data?.tasks?.data ?? []).map((task: Task) => {
      if (task) {
        return {
          ...task,
          amount: formatCurrency(task?.amount ?? 0, { ...data?.rewardAsset, locale })
        };
      }

      return null;
    })) as Array<Task>;

    const normalizedTask = {
      ...data?.task,
      amount: formatCurrency(data?.task?.amount ?? 0, { ...data?.rewardAsset, locale })
    };

    return {
      task: normalizedTask,
      tasks: normalizedTasks
    };
  }, [data, locale]);

  const {
    canDownload,
    downloadAppUrl,
    getSlykApp
  } = useSlykApp({
    code: code as string | null | undefined,
    onOpenModal: () => setModalOpen(true),
    slug
  });

  const { isNavbarFixed } = useWindowScroll();
  const { isBannerVisible, onClose } = useBanner();

  useEffect(() => {
    function handleResize() {
      const navbarHeight = document.querySelector('nav')?.offsetHeight ?? 0;

      setNavbarHeight(navbarHeight);
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!isLoading && !data?.task) {
    return <Redirect to={payspaceRoutes.home} />;
  }

  return (
    <>
      <Helmet titleTemplate={'%s'}>
        <title>
          {translate('title', { name })}
        </title>
      </Helmet>

      <Banner
        goToDashboard={dashboardUrlResolver(slug, routes.overview)}
        isVisible={isBannerVisible}
        onDismiss={onClose}
      />

      <Navbar
        downloadAppDisabled={isLoading}
        isFixed={isNavbarFixed}
        onGetApp={getSlykApp}
      />

      {!!isLoading && (
        <LoadingContainer navbarHeight={navbarHeight}>
          <StyledLoading
            active
            relative
          />
        </LoadingContainer>
      )}

      <SectionWrapper
        isVisible={!isLoading}
        navbarHeight={navbarHeight}
      >
        <TaskDetails
          getSlykApp={getSlykApp}
          task={task}
          tasks={tasks}
        />

        <Footer
          getSlykApp={getSlykApp}
          payspaceName={name}
          payspaceSlug={slug}
        />
      </SectionWrapper>

      {!canDownload && (
        <ModalPortal isOpen={isModalOpen}>
          <GetAppModal
            code={code as string | null | undefined}
            downloadAppUrl={downloadAppUrl}
            isVisible={isModalOpen}
            onRequestClose={closeModal}
          />
        </ModalPortal>
      )}
    </>
  );
}
